import { useEffect, useState } from 'react';
import request from 'lib/request';
import { nameToOption } from 'config/questionOptions';

const ATTRIBUTE_PARAMS = { select: ['id', 'name'], page: 1, pageSize: 0 };

/** Attribute list backing each measure source type. */
const SOURCE_ENDPOINTS = {
  wo: '/settings/wo-custom-attributes',
  company: '/settings/company-attributes',
  custom: '/settings/custom-attributes',
};

const EMPTY_OPTIONS = {};

// Module-level cache: the attribute name lists rarely change, and the widget
// mounts once per question in edit mode - N mounts collapse to one request.
let cachedOptions = null;
let pendingRequest = null;

const toNameOptions = (response) =>
  (response?.data ?? [])
    .map((attribute) => attribute?.name)
    .filter(Boolean)
    .map(nameToOption);

const loadOptions = () => {
  if (cachedOptions) return Promise.resolve(cachedOptions);

  pendingRequest =
    pendingRequest ??
    Promise.all(
      Object.entries(SOURCE_ENDPOINTS).map(([type, url]) =>
        request
          .get(url, { params: ATTRIBUTE_PARAMS })
          .then((response) => [type, toNameOptions(response)]),
      ),
    )
      .then((entries) => {
        cachedOptions = entries.reduce((options, [type, names]) => {
          options[type] = names;
          return options;
        }, {});
        return cachedOptions;
      })
      .catch(() => {
        pendingRequest = null;
        return EMPTY_OPTIONS;
      });

  return pendingRequest;
};

/**
 * Loads the attribute names available as a reference-date source for a
 * "Date" quantitative question: work object custom fields, company attributes
 * and asset custom attributes. Returns options keyed by source type.
 * Pass `enabled: false` to skip fetching (e.g. for non-Date questions).
 */
export default function useMeasureSourceAttributes(enabled = true) {
  const [options, setOptions] = useState(cachedOptions ?? EMPTY_OPTIONS);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    loadOptions().then((result) => {
      if (active) setOptions(result);
    });

    return () => {
      active = false;
    };
  }, [enabled]);

  return options;
}
